import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Badge, Button, Alert, Table, Form, Modal, Dropdown } from "react-bootstrap";
import {
  FaChevronLeft, FaChevronDown, FaChevronRight, FaFileInvoiceDollar,
  FaFilePdf, FaDownload, FaTrash, FaCommentDots, FaSave, FaSync,
  FaTruck, FaFileInvoice, FaExclamationTriangle, FaFileAlt, FaProjectDiagram,
} from "react-icons/fa";
import { apiFetch, hasAction } from "./utils/auth";
import { shipBlockReason as computeShipBlockReason, shipProgress } from "./utils/shipGate";
import AckStatusPanel from "./AckStatusPanel";
import AttachmentsButton from "./components/ReferenceDocsPanel";
import ReorderPartsModal from "./components/ReorderPartsModal";
import OrderChainPanel from "./components/OrderChainPanel";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// ── Formatting helpers ───────────────────────────────────────────────────────
// Money is shown in DOCUMENT currency using the order's own symbol (falls back
// to a plain number when Epicor doesn't supply one). Numbers only — no locale
// currency code guessing, since the symbol is authoritative.

function money(v, symbol) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
  return symbol ? `${symbol}${n}` : n;
}
function fmtQty(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(Number(v));
}
function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ── Small presentational bits ────────────────────────────────────────────────
function Card({ title, right, children, style }) {
  return (
    <div className="qw-card-flat qw-pad" style={{ marginBottom: 14, ...style }}>
      {(title || right) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {title && <span className="qw-eyebrow">{title}</span>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="qw-eyebrow" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: "#0F172A", whiteSpace: "pre-wrap" }}>{children ?? "—"}</div>
    </div>
  );
}
// One tile in the facts strip. Same eyebrow/value language as Field, but the
// value carries more weight and the tile can hold a sub-line. Spans are avoided
// deliberately: App.css declares a global `span { margin-left: 16px }` and the
// reset for it (DynamicQw.css) only exists inside a phone media query, so a
// span here would sit 16px in on desktop and flush on a phone.
function Fact({ label, value, sub, mono, big }) {
  return (
    <div className="ov-fact">
      <div className="qw-eyebrow" style={{ marginBottom: 3 }}>{label}</div>
      <div className={`ov-fact-v${mono ? " qw-mono" : ""}${big ? " ov-fact-big" : ""}`}>{value ?? "\u2014"}</div>
      {sub ? <div className="ov-fact-sub">{sub}</div> : null}
    </div>
  );
}
function TotalRow({ label, value, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 500 }}>
      <span style={{ color: strong ? "#0F172A" : "#64748B" }}>{label}</span>
      <span className="qw-mono" style={{ color: strong ? "#0052A5" : "#0F172A" }}>{value}</span>
    </div>
  );
}

// =============================================================================
// OrderView — read-only Sales Order page (post-conversion).
// One live GET powers header + lines + totals + parties + tax + schedule. The
// page can download PO docs & the acknowledgement PDF, resend the ack email
// (AckStatusPanel), add an order comment, and void the order — but only when
// nothing has shipped (the void guard is UI-side; the backend does not enforce
// it). Everything else about the order is read-only here.
// =============================================================================
function OrderView() {
  const { orderNum } = useParams();
  const navigate = useNavigate();

  const [data, setData]         = useState(null);   // SalesOrderSingleResponse.order (SalesOrderFullDTO)
  const [confs, setConfs]       = useState([]);     // OrderConfirmation rows (PO attachmentIds)
  const [orderAtts, setOrderAtts] = useState([]);   // PO docs linked directly to the order (fallback)
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [expanded, setExpanded] = useState({});     // orderLine -> bool
  const [actionErr, setActionErr] = useState(null);
  const [actionNote, setActionNote] = useState(null);

  // Comment editor
  const [editComment, setEditComment] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);

  // Ack PDF download
  // Holds the VARIANT being fetched, not a boolean. The Documents modal lists
  // all three ack layouts as rows, so a shared boolean would either light up all
  // three with "Preparing…" or — as it did after the modal landed — none of
  // them, because `downloading === "lines"` can never be true for `true`.
  const [downloading, setDownloading] = useState(null);

  // Void
  const [showVoid, setShowVoid] = useState(false);
  const [voiding, setVoiding]   = useState(false);

  // Documents modal — one home for everything file-shaped on this order, the
  // same pattern QuoteEdit uses. It replaces three ribbon controls and two
  // full-width cards: the Ack download menu, the Attachments button, the
  // Purchase orders list and the Acknowledgements panel.
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsTab, setDocsTab]   = useState("ack");

  // Related records — its OWN control, not a Documents tab. Documents holds
  // what EXISTS on the order as a file; this is the live Epicor relationship
  // map, a different question with a different answer time.
  const [chainOpen, setChainOpen] = useState(false);

  // Backorder parts (spec: backorder-ui-flow.html)
  // { partNum, qty } for the S9… line whose wizard is open. The QUANTITY is
  // carried too, not just the part number: a backorder can never exceed what the
  // customer is actually owed, which is the component's per-system quantity
  // times the number of systems on that line.
  const [reorderSystem, setReorderSystem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ordRes, confRes, attRes] = await Promise.all([
        apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}?include=shipments,invoices`),
        // Confirmations give us each PO's attachmentId with rich context
        // (confirmed PO#/amount, source quote). Best-effort — a failure here
        // must not block the order from rendering.
        apiFetch(`${API_URL}/api/OrderConfirmation/by-order/${encodeURIComponent(orderNum)}`).catch(() => null),
        // Fallback: PO docs linked directly to the order. Catches any PO
        // attachment that isn't tied to a confirmation. Best-effort.
        apiFetch(`${API_URL}/api/Attachment?orderNum=${encodeURIComponent(orderNum)}&attachmentType=PODocument`).catch(() => null),
      ]);
      const ord = await ordRes.json().catch(() => null);
      if (!ordRes.ok || !ord?.success || !ord?.order) {
        throw new Error(ord?.error || ord?.message || `Order ${orderNum} not found or not visible to you.`);
      }
      setData(ord.order);
      if (confRes) {
        const c = await confRes.json().catch(() => null);
        setConfs(Array.isArray(c?.confirmations) ? c.confirmations : []);
      }
      if (attRes) {
        const a = await attRes.json().catch(() => null);
        setOrderAtts(Array.isArray(a) ? a.filter((x) => x.status !== "Deleted") : []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orderNum]);

  useEffect(() => { load(); }, [load]);

  const header  = data?.header || {};
  const symbol  = header.currencyCodeCurrSymbol || "";
  const lines   = data?.lines || [];
  const headerMisc = data?.headerMiscCharges || [];
  const taxSummary = data?.taxSummary || [];
  const paySchedule = data?.paymentSchedule || [];
  const shipments = data?.linkedShipments;   // null unless include=shipments succeeded
  const invoices  = data?.linkedInvoices;

  // Unified PO-document list: confirmations first (richest context), then any
  // order-linked PO attachment not already represented by a confirmation.
  const poDocs = useMemo(() => {
    const docs = confs.map((c) => ({
      key: c.confirmationNumber || `att-${c.attachmentId}`,
      attachmentId: c.attachmentId,
      label: c.confirmedPONumber || c.customerPoNum || c.confirmationNumber,
      amount: c.confirmedPOAmount ?? c.amount,
      sub: c.sourceQuoteNum ? `from quote ${c.sourceQuoteNum}` : null,
    }));
    const seen = new Set(docs.map((d) => d.attachmentId).filter((x) => x != null));
    for (const a of orderAtts) {
      if (seen.has(a.id)) continue;
      docs.push({
        key: `att-${a.id}`,
        attachmentId: a.id,
        label: a.confirmedPONumber || a.extractedPONumber || a.fileName,
        amount: a.confirmedPOAmount ?? a.extractedPOAmount,
        sub: a.sourceQuoteNum ? `from quote ${a.sourceQuoteNum}` : null,
      });
    }
    return docs;
  }, [confs, orderAtts]);

  // Void guard (§1.4-E): open, not already void, and a *confirmed* empty
  // shipments array. null shipments = "unknown", never treat as voidable.
  // Shipping progress from release quantities — not header.totalShipped (a
  // money figure, which can't tell full from partial). Feeds both the header
  // badge and the void guard below, so it must be declared before them.
  const shipState = useMemo(() => shipProgress(data), [data]);

  // `shipments` alone can't gate this: it comes from an OData query that
  // fails on some tenants and yields [] — indistinguishable from "nothing
  // shipped". Release quantities are the reliable signal, so require BOTH
  // an empty shipment list and zero shipped quantity before allowing a void.
  const voidEnabled = useMemo(() => (
    header.openOrder === true &&
    header.voidOrder === false &&
    Array.isArray(shipments) && shipments.length === 0 &&
    shipState === "none"
  ), [header.openOrder, header.voidOrder, shipments, shipState]);

  const voidReason = !header.openOrder ? "Order is closed."
    : header.voidOrder ? "Order is already voided."
    : shipState === "full" ? "Cannot void — the order is fully shipped."
    : shipState === "partial" ? "Cannot void — part of this order has already shipped."
    : shipState === "unknown" ? "Shipped quantity unknown."
    : !Array.isArray(shipments) ? "Shipment status unknown."
    : shipments.length > 0 ? `Cannot void — ${shipments.length} shipment(s) exist.`
    : null;

  // ── Actions ────────────────────────────────────────────────────────────────
  const downloadAck = async (variant = "lines") => {
    setDownloading(variant); setActionErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reports/order/${encodeURIComponent(orderNum)}?variant=${variant}&format=pdf&download=true`);
      if (!r.ok) throw new Error(`Ack download failed (HTTP ${r.status}).`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Order-${orderNum}-ack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setActionErr(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const downloadPO = async (attachmentId) => {
    setActionErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/Attachment/${attachmentId}/download-url`);
      const d = await r.json().catch(() => null);
      const url = d?.downloadUrl || d?.url || d?.sasUrl;
      if (url) window.open(url, "_blank", "noopener");
      else throw new Error("No download link returned for that PO.");
    } catch (e) {
      setActionErr(e.message);
    }
  };

  const saveComment = async () => {
    setSavingComment(true); setActionErr(null); setActionNote(null);
    try {
      const r = await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}`, {
        method: "PATCH",
        body: JSON.stringify({ orderComment: commentText }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false) throw new Error(d?.error || d?.message || `Save failed (HTTP ${r.status}).`);
      setEditComment(false);
      setActionNote("Comment saved.");
      await load();   // refresh from the server (authoritative)
    } catch (e) {
      setActionErr(e.message);
    } finally {
      setSavingComment(false);
    }
  };

  const voidOrder = async () => {
    setVoiding(true); setActionErr(null); setActionNote(null);
    try {
      const r = await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}`, { method: "DELETE" });
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false) throw new Error(d?.error || d?.message || `Void failed (HTTP ${r.status}).`);
      setShowVoid(false);
      setActionNote("Order voided.");
      await load();
    } catch (e) {
      setActionErr(e.message);
      setShowVoid(false);
    } finally {
      setVoiding(false);
    }
  };

  // Why the order can't be shipped (credit hold / held / closed / fully
  // shipped). Shares utils/shipGate with the Stage-1 gate in ShipmentEntry so
  // the "Ship this order" button is disabled here too, before the wizard opens.
  const shipBlockReason = useMemo(() => computeShipBlockReason(data, "short"), [data]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
          <div>
            <div className="qw-header-title">Order #{orderNum}</div>
            <div className="qw-header-sub">{header.customerName || header.customerCustID || "Sales order"}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {hasAction("00067", true) && (
          <Button className="qw-btn qw-btn-sm" disabled={loading || !!shipBlockReason}
            title={shipBlockReason || "Generate FedEx label + ship this order"}
            style={{ background: "var(--fedex-orange, #FF6600)", color: "#fff", border: "none", fontWeight: 700, opacity: shipBlockReason ? 0.55 : 1 }}
            onClick={() => navigate(`/order/${encodeURIComponent(orderNum)}/ship`)}>
            <FaTruck style={{ marginRight: 6 }} /> Ship this order
          </Button>
          )}
          {/* ONE button for everything file-shaped. It used to be three — an Ack
              download menu, an Attachments button and, further down the page, two
              full-width cards listing POs and acknowledgements. On an order with
              ten POs and ten acks that was about twenty rows of scrolling to find
              a document. Same pattern as the quote page. */}
          <Button className="qw-btn qw-btn-sm" disabled={loading}
            style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            onClick={() => setDocsOpen(true)}>
            <FaFileAlt style={{ marginRight: 6 }} /> Documents
          </Button>
          {/* Attachments stays its OWN button, deliberately. It was briefly a
              fourth tab inside Documents, which forced a modal inside a modal:
              nested it stacked two backdrops, and closing the outer one to avoid
              that unmounted the button along with it, so neither dialog opened.
              The split is also the truer one — Documents holds what EXISTS on
              the order (the ack, the customer's POs, what was sent), while this
              is internal working material that never goes out. Gates on 00060. */}
          <AttachmentsButton entityType="Order" entityId={orderNum} />

          {/* Peer of Documents and Attachments. No entitlement gate here: the
              endpoint behind it gates on OrderView (00049), which is the same
              entitlement this route already requires — a gate here could only
              hide a button that works. */}
          <Button className="qw-btn qw-btn-sm" disabled={loading}
            title="The quote, shipments, invoices and payments Epicor links to this order"
            style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            onClick={() => setChainOpen(true)}>
            <FaProjectDiagram style={{ marginRight: 6 }} /> Related
          </Button>

          <Dropdown align="end">
            <Dropdown.Toggle className="qw-btn qw-btn-sm" disabled={loading}
              style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }} />
            <Dropdown.Menu>
              <Dropdown.Item onClick={load} disabled={loading}><FaSync style={{ marginRight: 8 }} /> Refresh</Dropdown.Item>
              <Dropdown.Item onClick={() => navigate(-1)}><FaChevronLeft style={{ marginRight: 8 }} /> Back</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {actionNote && <Alert variant="success" dismissible onClose={() => setActionNote(null)} style={{ fontSize: 13 }}>{actionNote}</Alert>}
      {actionErr && <Alert variant="danger" dismissible onClose={() => setActionErr(null)} style={{ fontSize: 13 }}>{actionErr}</Alert>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading order…</div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>
      ) : (
        <>
          {/* Status badges */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <Badge bg={header.voidOrder ? "danger" : header.openOrder ? "success" : "secondary"}>
              {header.voidOrder ? "Voided" : header.openOrder ? "Open" : "Closed"}
            </Badge>
            {header.orderStatus && <span className="qw-pill qw-pill-slate">{header.orderStatus}</span>}
            {header.orderHeld && <Badge bg="warning" text="dark">Held</Badge>}
            {header.custOnCreditHold && <Badge bg="danger">Credit hold</Badge>}
            {header.orderType_c && <span className="qw-pill qw-pill-slate">{header.orderType_c}</span>}
            {header.rentalOrder_c && <span className="qw-pill qw-pill-indigo">Rental</span>}
            {shipState === "full" && (
              <Badge bg="info" title="Every line release has its full quantity shipped.">Shipped</Badge>
            )}
            {shipState === "partial" && (
              <Badge bg="warning" text="dark" title="Some quantity has shipped; the order still has open quantity remaining.">
                Partially shipped
              </Badge>
            )}
            {header.invoicesExist && <Badge bg="info">Invoiced</Badge>}
          </div>

          {header.creditLimitMessage && (
            <Alert variant="warning" style={{ fontSize: 12.5 }}>{header.creditLimitMessage}</Alert>
          )}

          {/* ── Facts · parties · terms — THREE FULL-WIDTH BANDS, not a grid of
                 peers, and not a two-column split.
                 Four equal cards (Order summary | Totals, Ship to | Bill to) gave
                 the page no hierarchy: the reference data and the money carried the
                 same weight, and the lines — what the order IS — sat below all of
                 it. Bands rather than columns is QuoteEdit's finding, not a fresh
                 opinion: "the identity/facts block and the pricing roll-up carry
                 very different amounts of content, so side-by-side always left a
                 hole" (QuoteEdit.js, above its own two bands). The same is true
                 here, and there is a second reason — the lines table declares
                 min-width 760px, which does not fit a two-thirds column until
                 roughly 1600px of viewport.
                 Every field the four cards held is still on this page; only where
                 it sits has changed. */}

          {/* The five things someone opens this page to check. qw-grid-auto is
              repeat(auto-fit, minmax(220px,1fr)) — it reflows from 5-up to 1-up on
              its own, so this needs no media query at any width. */}
          <Card>
            <div className="qw-grid-auto ov-facts">
              <Fact label="Order total" value={money(header.docOrderAmt, symbol)} sub={header.currencyCode} mono big />
              <Fact label="Customer PO" value={header.poNum} mono />
              <Fact label="Order date" value={fmtDate(header.orderDate)} />
              <Fact label="Sales rep" value={header.salesRepName1 || header.salesRepCode1} />
              {/* `invoices?.length`, not `invoices.length`: the variable is
                  deliberately left undefined when the include fails (see its
                  declaration) — null means "not looked up", not "none". Without
                  the optional chain this throws on exactly the order that HAS
                  invoices but whose enrichment came back empty. */}
              <Fact label="Invoiced" value={header.invoicesExist ? "Yes" : "No"}
                    sub={header.invoicesExist && invoices?.length ? `${invoices.length} invoice(s)` : null} />
            </div>
          </Card>

          {/* ── Comments — high on the page, not buried ─────────────────────
              It sat eighth of nine cards, under tax and payment tables. It is
              context a rep needs BEFORE they act — before shipping, before
              converting — so it belongs where they will actually read it.

              One caveat worth knowing: this field is mixed-use. On 155330/155331
              it holds a real note ("Multi-ship partial-conversion pilot"); on
              155335 it holds machine state copied from the quote at conversion
              ("customerContact=3, shipToContact=1, shipTo=0"). Promoting it means
              that plumbing is now near the top on those orders. Recognising and
              folding that away is a separate change. ── */}
          <Card title="Comments" right={
            !editComment && hasAction("00064", true) && (
              <Button size="sm" variant="outline-secondary" onClick={() => { setCommentText(header.orderComment || ""); setEditComment(true); }}>
                <FaCommentDots style={{ marginRight: 4 }} /> {header.orderComment ? "Edit" : "Add"} comment
              </Button>
            )
          }>
            {editComment ? (
              <div>
                <Form.Control as="textarea" rows={3} value={commentText} onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Order comment visible on the order header." />
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <Button variant="primary" size="sm" disabled={savingComment} onClick={saveComment}>
                    {savingComment ? <><Spinner animation="border" size="sm" /> Saving…</> : <><FaSave style={{ marginRight: 4 }} /> Save</>}
                  </Button>
                  <Button variant="outline-secondary" size="sm" disabled={savingComment} onClick={() => setEditComment(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <Field label="Order comment">{header.orderComment}</Field>
                {header.shipComment && <Field label="Ship comment">{header.shipComment}</Field>}
                {header.invoiceComment && <Field label="Invoice comment">{header.invoiceComment}</Field>}
              </>
            )}
          </Card>

          {/* All three parties in one band. They were two cards, and on orders
              where bill-to is flagged "Same As Sold To" that read as a duplicate —
              but it is not one: bill-to equals SOLD-TO, which is a third party the
              page never named. Side by side the differences are visible. */}
          <Card title="Customer">
            <div className="qw-grid-auto">
              <div>
                <Field label="Sold to">{header.customerName} {header.customerCustID ? `(${header.customerCustID})` : ""}</Field>
              </div>
              <div>
                <Field label={header.shipToNumName || "Ship-to"}>{header.shipToAddressFormatted || header.otsName}</Field>
                {header.shipToContactName && <Field label="Contact">{header.shipToContactName}</Field>}
                {(header.shipToContactEMailAddress || header.shipToContactPhoneNum) && (
                  <div style={{ fontSize: 12.5, color: "#64748B" }}>
                    {header.shipToContactEMailAddress}{header.shipToContactEMailAddress && header.shipToContactPhoneNum ? " · " : ""}{header.shipToContactPhoneNum}
                  </div>
                )}
              </div>
              <div>
                <Field label={header.billToCustomerName || header.customerBTName || "Bill-to"}>{header.btAddressFormatted}</Field>
                {header.btContactName && <Field label="Contact">{header.btContactName}</Field>}
                {header.btContactEMailAddress && <div style={{ fontSize: 12.5, color: "#64748B" }}>{header.btContactEMailAddress}</div>}
              </div>
            </div>
          </Card>

          {/* Terms & shipping — now also the home for Request date and Need by,
              the two dates that say when the customer expects goods. They were in
              Order summary; this is where the rest of the fulfilment terms live. */}
          <Card title="Terms & shipping">
            <div className="ov-fieldgrid" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 160px" }}><Field label="Terms">{header.termsCodeDescription || header.termsCode}</Field></div>
              <div style={{ flex: "1 1 160px" }}><Field label="Ship via">{header.shipViaCodeDescription || header.shipViaCode}</Field></div>
              <div style={{ flex: "1 1 120px" }}><Field label="Request date">{fmtDate(header.requestDate)}</Field></div>
              <div style={{ flex: "1 1 120px" }}><Field label="Need by">{fmtDate(header.needByDate)}</Field></div>
              <div style={{ flex: "1 1 120px" }}><Field label="FOB">{header.fobDescription || header.fob}</Field></div>
              <div style={{ flex: "1 1 120px" }}><Field label="Currency">{header.currencyCode}</Field></div>
              {header.incotermCode && <div style={{ flex: "1 1 160px" }}><Field label="Incoterm">{header.incotermsDescription || header.incotermCode}</Field></div>}
            </div>
          </Card>

          {/* Lines.
              `ov-lines` + the data-label on each cell drive a PHONE-ONLY restack
              (see .ov-lines rules in DynamicQw.css): under 992px the 760px-wide
              table becomes one card per line, because on a 440px screen the last
              five columns (Disc %, Ext price, Tax, Total, Status) sat off-screen
              and were only reachable by a sideways swipe most reps never find.
              data-* attributes render nothing, so the desktop table is untouched. */}
          <Card title={`Lines (${lines.length})`}>
            <div style={{ overflowX: "auto" }} className="ov-lines">
              <Table hover size="sm" style={{ fontSize: 13, marginBottom: 0, minWidth: 760 }}>
                <thead>
                  <tr style={{ color: "#64748B", fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    <th style={{ width: 30 }}></th>
                    <th>#</th>
                    <th>Part / Description</th>
                    <th className="text-end">Qty</th>
                    <th>UM</th>
                    <th className="text-end">Unit price</th>
                    <th className="text-end">Disc %</th>
                    <th className="text-end">Ext price</th>
                    <th className="text-end">Tax</th>
                    <th className="text-end">Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={11} style={{ color: "#94A3B8", padding: 16 }}>No lines on this order.</td></tr>
                  )}
                  {lines.map((ln) => {
                    const hasDetail = (ln.releases?.length || 0) + (ln.miscCharges?.length || 0) + (ln.releaseTaxes?.length || 0) > 0;
                    const open = !!expanded[ln.orderLine];
                    return (
                      <React.Fragment key={ln.orderLine}>
                        <tr>
                          <td>
                            {hasDetail && (
                              <Button variant="link" size="sm" style={{ padding: 0, color: "#64748B" }}
                                onClick={() => setExpanded((e) => ({ ...e, [ln.orderLine]: !open }))}>
                                {open ? <FaChevronDown /> : <FaChevronRight />}
                              </Button>
                            )}
                          </td>
                          <td className="qw-mono" data-label="Line">{ln.orderLine}</td>
                          <td style={{ maxWidth: 280 }} data-label="Part">
                            <div style={{ fontWeight: 600, color: "#0F172A" }}>
                              {ln.partNum || "—"}
                              {(ln.orderComment || "").startsWith("Back order discount given") && (
                                <Badge bg="info" style={{ marginLeft: 6, fontSize: 10 }}
                                  title="Backorder line — $0, ships separately; its $0 invoice is auto-suppressed (no email, no reminder, no amount due)">
                                  Backorder · No-charge
                                </Badge>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: "#64748B" }}>{ln.lineDesc || ln.partNumPartDescription}</div>
                            {/* BACKORDER, not "reorder" — the two are different
                                things and only this label was wrong. A backorder
                                is the CUSTOMER's order for goods that ship later;
                                a reorder is the company restocking from a vendor.
                                This button adds $0 components to the customer's
                                order for a separate shipment, which is the first.
                                The spec (backorder-ui-flow), the endpoint
                                (/backorder-lines) and the line badge
                                ("Backorder · No-charge") all already said so.
                                Gating is unchanged: S9… system lines, order open
                                and not void, entitlement 00064. */}
                            {(ln.partNum || "").toUpperCase().startsWith("S9") && header.openOrder && !header.voidOrder && hasAction("00064", true) && (
                              <Button size="sm" variant="outline-info" style={{ marginTop: 4, fontSize: 11, padding: "2px 8px", fontWeight: 700 }}
                                onClick={() => setReorderSystem({
                                  partNum: ln.partNum,
                                  qty: Number(ln.sellingQuantity ?? ln.orderQty) || 1,
                                })}>
                                + Backorder parts
                              </Button>
                            )}
                          </td>
                          <td className="text-end qw-mono" data-label="Qty">{fmtQty(ln.sellingQuantity ?? ln.orderQty)}</td>
                          <td data-label="UM">{ln.salesUM}</td>
                          <td className="text-end qw-mono" data-label="Unit price">{money(ln.docUnitPrice, symbol)}</td>
                          <td className="text-end qw-mono" data-label="Disc %">{ln.discountPercent ? `${fmtQty(ln.discountPercent)}%` : "—"}</td>
                          <td className="text-end qw-mono" data-label="Ext price">{money(ln.docExtPriceDtl, symbol)}</td>
                          <td className="text-end qw-mono" data-label="Tax">{money(ln.docTaxAmt, symbol)}</td>
                          <td className="text-end qw-mono" style={{ fontWeight: 600 }} data-label="Total">{money(ln.docTotalPrice, symbol)}</td>
                          <td data-label="Status">
                            <Badge bg={ln.voidLine ? "danger" : ln.openLine ? "success" : "secondary"}>
                              {ln.voidLine ? "Void" : ln.openLine ? "Open" : "Closed"}
                            </Badge>
                          </td>
                        </tr>
                        {open && hasDetail && (
                          <tr>
                            <td></td>
                            <td colSpan={10} style={{ background: "#F8FAFC" }}>
                              {ln.miscCharges?.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                  <div className="qw-eyebrow" style={{ marginBottom: 4 }}>Line misc charges</div>
                                  {ln.miscCharges.map((m) => (
                                    <div key={m.seqNum} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "1px 0" }}>
                                      <span>{m.description || m.miscCodeDescription || m.miscCode}</span>
                                      <span className="qw-mono">{money(m.docMiscAmt, symbol)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {ln.releaseTaxes?.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                  <div className="qw-eyebrow" style={{ marginBottom: 4 }}>Line tax</div>
                                  {ln.releaseTaxes.map((t, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "1px 0" }}>
                                      <span>{t.taxCode}{t.rateCode ? ` · ${t.rateCode}` : ""} {t.percent ? `(${fmtQty(t.percent)}%)` : ""}</span>
                                      <span className="qw-mono">{money(t.docTaxAmt, symbol)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {ln.releases?.length > 0 && (
                                <div>
                                  <div className="qw-eyebrow" style={{ marginBottom: 4 }}>Releases</div>
                                  {ln.releases.map((rel, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "1px 0" }}>
                                      <span>Rel {rel.orderRelNum ?? i + 1} · Need by {fmtDate(rel.needByDate)}</span>
                                      <span className="qw-mono">{fmtQty(rel.relQty ?? rel.ourReqQty)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* ── The money, directly beneath the lines it comes from ──────────
                Was a "Totals" card sitting beside "Order summary", which is what
                made it read as a list rather than a sum. Three things changed and
                none of them is a new number:
                  1. The DUPLICATE is gone. "Order amount" (first row) and
                     "Order total" (last row) were both header.docOrderAmt — the
                     card opened with its own answer and then listed the parts.
                  2. DISCOUNT IS SIGNED. docTotalDiscount is a positive magnitude
                     that SUBTRACTS; unsigned it was untidy in a list and wrong in
                     a block that has to foot.
                  3. The order is the arithmetic: charges, less discount, plus tax,
                     plus misc = the total. On order 155335 that is
                     95,780.00 − 26,942.42 + 4,252.71 + 20.00 = 73,110.29, which is
                     docOrderAmt exactly.
                "Charges" keeps its name deliberately. It is GROSS ext price, while
                the acknowledgement PDF foots on NET — LinesTotal = Σ(DocExtPriceDtl
                − DocDiscount) — so calling it "Subtotal" here would print a
                different subtotal from the document the customer is holding.
                The old "· Shipped $0 · Invoiced $0" footnote is gone: Epicor leaves
                TotalShipped at 0 until an order is INVOICED (SalesOrderRepository
                says so in as many words), so on a shipped-but-uninvoiced order it
                contradicted the "Partially shipped" badge with a wrong number. The
                badge is derived from release quantities and stays. */}
            <div className="ov-foot">
              <TotalRow label="Charges" value={money(header.docTotalCharges, symbol)} />
              <TotalRow label="Discount" value={`\u2212${money(header.docTotalDiscount, symbol)}`} />
              <TotalRow label="Tax" value={`+${money(header.docTotalTax, symbol)}`} />
              <TotalRow label="Misc" value={`+${money(header.docTotalMisc, symbol)}`} />
              <div className="ov-foot-total">
                <TotalRow label="Order total" value={money(header.docOrderAmt, symbol)} strong />
              </div>
              {header.currencyCode && <div className="ov-foot-note">Amounts in {header.currencyCode}</div>}
            </div>
          </Card>

          {/* ── Money detail ─────────────────────────────────────────────────
              Three cards became one. They answer the same question — why does
              the order total look like that — and each held a handful of rows in
              a full-width card, which is most of why the page read as a long thin
              scroll. The two TABLES are behind disclosures because both repeat:
              a payment schedule is often ten identical instalments, and the tax
              rows only matter when someone is reconciling.

              Tax summary is NOT dropped, though it looks redundant on a one-line
              order where the line's own tax rows carry the same two numbers. It
              is a different cut of the same money — per JURISDICTION, where the
              line detail is per LINE. On order 155335: summary reads PINELLAS 1%
              $121.25 + FLORIDA 6% $4,131.46; the six lines read 2,451.39 /
              1,653.97 / 33.60 / 45.50 / 45.50 / 22.75. Same $4,252.71, and only
              the summary says which state charged what. ── */}
          {(headerMisc.length > 0 || taxSummary.length > 0 || paySchedule.length > 0) && (
            <Card title="Money detail">
              {headerMisc.length > 0 && (
                <div className="ov-md-block">
                  <div className="ov-md-head"><span className="ov-md-t">Header misc charges</span></div>
                  {headerMisc.map((m) => (
                <div key={m.seqNum} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderTop: "1px solid #f1f5f9" }}>
                  <span>{m.description || m.miscCodeDescription || m.miscCode}{m.type === "P" && m.percentage ? ` (${fmtQty(m.percentage)}%)` : ""}</span>
                  <span className="qw-mono">{money(m.docMiscAmt, symbol)}</span>
                </div>
              ))}
                </div>
              )}

              {taxSummary.length > 0 && (
                <details className="ov-md-block ov-md-disc">
                  <summary>
                    <span className="ov-md-t">Tax summary</span>
                    <span className="ov-md-sum">{taxSummary.length} jurisdiction{taxSummary.length === 1 ? "" : "s"} · {money(header.docTotalTax, symbol)}</span>
                  </summary>
                  <div style={{ overflowX: "auto" }}>
                <Table size="sm" style={{ fontSize: 12.5, marginBottom: 0 }}>
                  <thead>
                    <tr style={{ color: "#64748B", fontSize: 11, textTransform: "uppercase" }}>
                      <th>Tax code</th><th>Rate</th><th className="text-end">%</th>
                      <th className="text-end">Taxable</th><th className="text-end">Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxSummary.map((t, i) => (
                      <tr key={i}>
                        <td>{t.taxCode}{t.taxDescription ? ` — ${t.taxDescription}` : ""}</td>
                        <td>{t.rateCode || "—"}</td>
                        <td className="text-end qw-mono">{fmtQty(t.percent)}</td>
                        <td className="text-end qw-mono">{money(t.docTaxableAmt, symbol)}</td>
                        <td className="text-end qw-mono">{money(t.docTaxAmt, symbol)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
                </details>
              )}

              {paySchedule.length > 0 && (
                <details className="ov-md-block ov-md-disc">
                  <summary>
                    <span className="ov-md-t">Payment schedule</span>
                    <span className="ov-md-sum">
                      {paySchedule.length} instalment{paySchedule.length === 1 ? "" : "s"}
                      {paySchedule.length > 1 ? ` · ${money(paySchedule[0].docPayAmount, symbol)} each` : ` · ${money(paySchedule[0].docPayAmount, symbol)}`}
                    </span>
                  </summary>
                  <div style={{ overflowX: "auto" }}>
                <Table size="sm" style={{ fontSize: 12.5, marginBottom: 0 }}>
                  <thead>
                    <tr style={{ color: "#64748B", fontSize: 11, textTransform: "uppercase" }}>
                      <th>#</th><th className="text-end">Days</th><th className="text-end">%</th><th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paySchedule.map((p) => (
                      <tr key={p.paySeq}>
                        <td className="qw-mono">{p.paySeq}{p.isLastRow ? " (last)" : ""}</td>
                        <td className="text-end qw-mono">{p.payDays}</td>
                        <td className="text-end qw-mono">{fmtQty(p.payPercent)}</td>
                        <td className="text-end qw-mono">{money(p.docPayAmount, symbol)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
                </details>
              )}
            </Card>
          )}

          {/* Purchase orders and Acknowledgements used to be two full-width cards
              here. Both are now tabs in the Documents modal — see the ribbon. */}

          {/* Linked shipments / invoices */}
          {(Array.isArray(shipments) && shipments.length > 0) && (
            <Card title={`Shipments (${shipments.length})`}>
              {shipments.map((s) => (
                <div key={s.packNum} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, padding: "5px 0", borderTop: "1px solid #f1f5f9", gap: 10, flexWrap: "wrap" }}>
                  <span><FaTruck style={{ color: "#64748B", marginRight: 6 }} />Pack #{s.packNum} · {s.shipStatus || (s.voided ? "Voided" : "—")}{s.trackingNumber ? ` · ${s.trackingNumber}` : ""}</span>
                  <span className="qw-mono" style={{ color: "#94A3B8" }}>{fmtDate(s.shipDate)}{s.invoiced ? " · invoiced" : ""}</span>
                </div>
              ))}
            </Card>
          )}
          {(Array.isArray(invoices) && invoices.length > 0) && (
            <Card title={`Invoices (${invoices.length})`}>
              {invoices.map((iv) => (
                <div key={iv.invoiceNum} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, padding: "5px 0", borderTop: "1px solid #f1f5f9", gap: 10, flexWrap: "wrap" }}>
                  <span><FaFileInvoice style={{ color: "#64748B", marginRight: 6 }} />Invoice #{iv.invoiceNum}</span>
                  <span className="qw-mono" style={{ color: "#94A3B8" }}>{money(iv.docInvoiceAmt ?? iv.invoiceAmt, symbol)} · {fmtDate(iv.invoiceDate)}</span>
                </div>
              ))}
            </Card>
          )}

          {/* Danger zone — void. Entitlement 00066 (server enforces too). */}
          {hasAction("00066", true) && (
          <Card title="Void order">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12.5, color: "#64748B", maxWidth: 620 }}>
                Voiding deletes the order in Epicor. Allowed only while the order is open and nothing has shipped.
                {voidReason && <span style={{ color: "#B45309", display: "block", marginTop: 4 }}><FaExclamationTriangle style={{ marginRight: 6 }} />{voidReason}</span>}
              </div>
              <Button variant="outline-danger" disabled={!voidEnabled} title={voidReason || undefined} onClick={() => setShowVoid(true)}>
                <FaTrash style={{ marginRight: 6 }} /> Void order
              </Button>
            </div>
          </Card>
          )}

          {data.generatedAtUtc && (
            <div style={{ fontSize: 11, color: "#CBD5E1", textAlign: "right", marginTop: 4 }}>
              <FaSync style={{ marginRight: 4 }} />Live from Epicor · {fmtWhen(data.generatedAtUtc)}
            </div>
          )}
        </>
      )}

      {/* ── Documents modal ────────────────────────────────────────────────
          Four tabs rather than one scroll, because these four things have
          different owners and different directions of travel: the acknowledgement
          goes OUT to the customer, their POs come IN, the ack history is a record
          of what was sent, and attachments are internal and never sent.
          Icons stay in one indigo family — the quote page learned that colouring
          them red/indigo/green reads as three severities, not three categories. */}
      <Modal show={docsOpen} onHide={() => setDocsOpen(false)} size="lg" centered scrollable
             dialogClassName="qe-docs-modal">
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: "-.01em" }}>Documents</Modal.Title>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              Order #{orderNum} · {header.customerName || header.customerCustID}
            </div>
          </div>
        </Modal.Header>

        <div className="qe-tabs">
          <button type="button" className={`qe-tab${docsTab === "ack" ? " on" : ""}`} onClick={() => setDocsTab("ack")}>
            <span className="qe-tab-long">Acknowledgement</span><span className="qe-tab-short">Ack</span> <span className="c">3</span>
          </button>
          <button type="button" className={`qe-tab${docsTab === "po" ? " on" : ""}`} onClick={() => setDocsTab("po")}>
            <span className="qe-tab-long">Customer POs</span><span className="qe-tab-short">POs</span> <span className="c">{poDocs.length}</span>
          </button>
          <button type="button" className={`qe-tab${docsTab === "sent" ? " on" : ""}`} onClick={() => setDocsTab("sent")}>
            <span className="qe-tab-long">Sent history</span><span className="qe-tab-short">Sent</span>
          </button>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          {docsTab === "ack" && (
            <>
              <div className="qe-intro">
                Three layouts of the same order — pick what the customer should see.
                Generated on download, so each one always reflects the order as it stands now.
              </div>
              {[
                { v: "lines",    n: "Line detail",     d: "Every line, priced. The usual choice." },
                { v: "system",   n: "System summary",  d: "Systems only — component lines folded in." },
                { v: "discount", n: "Discount detail", d: "Shows list price against what was charged." },
              ].map((o) => (
                <button key={o.v} type="button" className="qe-doc" disabled={!!downloading}
                        onClick={() => downloadAck(o.v)}>
                  <span className="ic"><FaFilePdf /></span>
                  <span className="m">
                    <span className="n">{o.n}</span>
                    <span className="d">{downloading === o.v ? "Preparing…" : o.d}</span>
                  </span>
                  <span className="go"><FaDownload /></span>
                </button>
              ))}
            </>
          )}

          {docsTab === "po" && (
            poDocs.length === 0 ? (
              <div className="qe-empty">
                <span className="big"><FaFilePdf /></span>
                No PO documents linked to this order.
              </div>
            ) : (
              poDocs.map((d) => (
                <button key={d.key} type="button" className="qe-doc" disabled={!d.attachmentId}
                        onClick={() => downloadPO(d.attachmentId)}>
                  <span className="ic"><FaFilePdf /></span>
                  <span className="m">
                    <span className="n">{d.label}</span>
                    <span className="d">{money(d.amount, symbol)}{d.sub ? ` · ${d.sub}` : ""}</span>
                  </span>
                  <span className="go"><FaDownload /></span>
                </button>
              ))
            )
          )}

          {docsTab === "sent" && <AckStatusPanel orderNum={orderNum} bare />}

        </Modal.Body>

        <div className="qe-docs-foot">
          <span className="note">
            {docsTab === "ack"  && "Generated on download — always reflects the current order."}
            {docsTab === "po"   && (poDocs.length > 0 ? `${poDocs.length} document${poDocs.length === 1 ? "" : "s"}` : "POs are uploaded from the quote or the conversion screen.")}
            {docsTab === "sent" && "Every acknowledgement email sent for this order."}
          </span>
          <Button size="sm" className="qe-docs-pri" onClick={() => setDocsOpen(false)}>Done</Button>
        </div>
      </Modal>

      {/* ── Related records ───────────────────────────────────────────────────
          Its own dialog, not a Documents tab, for the same reason Attachments is
          its own button: Documents is a shelf of files that already exist, this
          is a live ~200-350ms Epicor RPC answering a different question. Reuses
          the qe-docs skin with no tab strip — nothing in it requires tabs. */}
      <Modal show={chainOpen} onHide={() => setChainOpen(false)} size="lg" centered scrollable
             dialogClassName="qe-docs-modal">
        <Modal.Header closeButton>
          {/* The wrapper div is load-bearing: .qe-docs-modal re-declares
              .btn-close { margin: 0 0 0 auto } and without it the X parks
              next to the title. */}
          <div>
            <Modal.Title style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: "-.01em" }}>Related records</Modal.Title>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              Order #{orderNum} · {header.customerName || header.customerCustID}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: 0 }}>
          <div className="qe-intro">
            Everything Epicor links to <strong>order #{orderNum}</strong> — the quote it came
            from, its shipments, invoices and payments.
          </div>
          {/* A class, not an inline style: the gutter narrows at 575.98px the way
              .qe-intro does, and an inline padding would outrank that query. */}
          <div className="qchain-host">
            {/* Mounted only while the dialog is open, so the live RPC stays off
                the order-open path exactly as the tab kept it. */}
            {chainOpen && (
              <OrderChainPanel orderNum={orderNum} navigate={navigate}
                               hereKind="order" hereRef={orderNum} bare />
            )}
          </div>
        </Modal.Body>

        {/* A plain div, not Modal.Footer — .modal-footer adds its own padding
            plus `> * { margin: .25rem }`, which fights .qe-docs-foot's gap. */}
        <div className="qe-docs-foot">
          <span className="note">
            Live from Epicor — the quote, shipments, invoices and payments linked to this order.
          </span>
          <Button size="sm" className="qe-docs-pri" onClick={() => setChainOpen(false)}>Done</Button>
        </div>
      </Modal>

      {/* Backorder / reorder-parts wizard */}
      <ReorderPartsModal
        show={!!reorderSystem}
        onHide={() => setReorderSystem(null)}
        orderNum={orderNum}
        systemPart={reorderSystem?.partNum}
        systemQty={reorderSystem?.qty}
        onDone={(msg, addedAny) => { setActionNote(msg); if (addedAny) load(); }}
      />

      {/* Void confirm modal */}
      <Modal show={showVoid} onHide={voiding ? undefined : () => setShowVoid(false)} centered>
        <Modal.Header closeButton={!voiding}><Modal.Title style={{ fontSize: 18 }}>Void order #{orderNum}?</Modal.Title></Modal.Header>
        <Modal.Body style={{ fontSize: 14 }}>
          This removes the order from Epicor and cannot be undone. Confirm there are no shipments or invoices against it.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={voiding} onClick={() => setShowVoid(false)}>Cancel</Button>
          <Button variant="danger" disabled={voiding} onClick={voidOrder}>
            {voiding ? <><Spinner animation="border" size="sm" /> Voiding…</> : "Void order"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OrderView;
